import { jsPDF } from "jspdf";

// Default brand palette (used when settings.receipt_config is missing fields)
const DEFAULTS = {
    primary_color: "#B8923A",   // gold
    dark_color: "#2C2C30",
    text_color: "#3C3C3C",
    muted_color: "#828282",
    cream_color: "#FAF6EE",
    header_brand: "Terra Boutique",
    header_subtitle: "EL SALVADOR · BOUTIQUE PREMIUM",
    title_pedido: "Comprobante de Pedido",
    title_apartado: "Comprobante de Apartado",
    body_font: "helvetica",        // helvetica | times | courier
    display_font: "times",         // headings
    base_font_size: 9,             // 8-12
    show_social_block: true,
    social_block_title: "Síguenos y mantente al tanto de nuestras novedades",
    social_lines: [],              // free-form list { label, value, show }
    show_bank_block: true,
    bank_block_title: "INSTRUCCIONES DE PAGO POR TRANSFERENCIA",
    footer_text: "Gracias por elegir Terra Boutique",
    footer_subtext: "",            // optional secondary footer line
};

const hexToRgb = (hex) => {
    if (!hex) return [0, 0, 0];
    const h = hex.replace("#", "");
    const f = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
    return [parseInt(f.slice(0, 2), 16), parseInt(f.slice(2, 4), 16), parseInt(f.slice(4, 6), 16)];
};
const lighten = (rgb, amt = 40) => rgb.map(c => Math.min(255, c + amt));
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export async function generateReceipt(order, settings = {}, _options = {}) {
    const cfg = { ...DEFAULTS, ...(settings.receipt_config || {}) };
    const GOLD = hexToRgb(cfg.primary_color);
    const DARK = hexToRgb(cfg.dark_color);
    const TEXT = hexToRgb(cfg.text_color);
    const MUTED = hexToRgb(cfg.muted_color);
    const CREAM = hexToRgb(cfg.cream_color);
    const BODY = cfg.body_font || "helvetica";
    const DISP = cfg.display_font || "times";
    const FS = Number(cfg.base_font_size) || 9;

    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    let y = 0;

    // Header band
    doc.setFillColor(...DARK);
    doc.rect(0, 0, W, 38, "F");
    doc.setFillColor(...GOLD);
    doc.rect(0, 38, W, 1.2, "F");

    // Brand
    doc.setFont(DISP, "bold");
    doc.setFontSize(26);
    doc.setTextColor(255, 255, 255);
    doc.text(cfg.header_brand, 15, 20);
    doc.setFont(BODY, "normal");
    doc.setFontSize(FS);
    doc.setTextColor(...lighten(GOLD, 60));
    doc.text(cfg.header_subtitle, 15, 27);

    // Right side: doc title
    doc.setFont(DISP, "italic");
    doc.setFontSize(18);
    doc.setTextColor(255, 255, 255);
    const title = order.payment_method === "apartado" ? (cfg.title_apartado || "Comprobante de Apartado") : (cfg.title_pedido || "Comprobante de Pedido");
    doc.text(title, W - 15, 22, { align: "right" });

    y = 50;

    // Order ID
    doc.setFillColor(...CREAM);
    doc.rect(15, y, W - 30, 18, "F");
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.3);
    doc.rect(15, y, W - 30, 18);

    doc.setFont(BODY, "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text("ID DEL PEDIDO", 20, y + 6);
    doc.setFont("courier", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...DARK);
    doc.text(order._id || order.order_id || "", 20, y + 13);

    doc.setFont(BODY, "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text("FECHA", W - 20, y + 6, { align: "right" });
    doc.setFont(BODY, "bold");
    doc.setFontSize(10);
    doc.setTextColor(...DARK);
    const date = order.created_at ? new Date(order.created_at).toLocaleString("es-SV", { dateStyle: "long", timeStyle: "short" }) : "";
    doc.text(date, W - 20, y + 13, { align: "right" });

    y += 28;

    // Customer + Payment info (two columns)
    const colW = (W - 30 - 5) / 2;
    const drawSection = (ttl, x, yy, w, rows) => {
        doc.setFont(BODY, "bold");
        doc.setFontSize(7);
        doc.setTextColor(...GOLD);
        doc.text(ttl, x, yy);
        doc.setFont(BODY, "normal");
        doc.setTextColor(...DARK);
        let dy = yy + 5;
        rows.forEach(([t, bold, wrap]) => {
            doc.setFontSize(bold ? 11 : FS);
            doc.setFont(BODY, bold ? "bold" : "normal");
            if (wrap) {
                const lines = doc.splitTextToSize(t || "", w - 4);
                lines.forEach(ln => { doc.text(ln, x, dy); dy += 4.5; });
            } else {
                doc.text(t || "", x, dy);
                dy += 5;
            }
        });
    };

    drawSection("CLIENTE", 15, y, colW, [
        [`${order.customer_name || ""} ${order.customer_lastname || ""}`.trim() || "—", true],
        [order.user_email || "", false],
        [order.customer_phone || "Sin teléfono", false],
    ]);

    drawSection("PAGO Y ENTREGA", 15 + colW + 5, y, colW, [
        [`Método: ${(order.payment_method || "").toUpperCase()}`, true],
        [order.needs_shipping ? "Envío a domicilio" : "Retiro en tienda", false],
        [`Estado: ${order.status || "pending"}`, false],
    ]);

    y += 32;

    // Tracking / Guía block — appears once the admin has assigned a C807 guía.
    // For transferencia orders this shows up after admin confirms the deposit.
    const guia = order.c807_guia || order.tracking_number;
    if (guia && order.needs_shipping) {
        doc.setFillColor(...CREAM);
        doc.rect(15, y, W - 30, 16, "F");
        doc.setDrawColor(...GOLD);
        doc.setLineWidth(0.4);
        doc.rect(15, y, W - 30, 16);
        doc.setFont(BODY, "bold"); doc.setFontSize(7); doc.setTextColor(...GOLD);
        doc.text(`NÚMERO DE GUÍA · ${order.tracking_provider || "C807"}`, 19, y + 5);
        doc.setFont("courier", "bold"); doc.setFontSize(12); doc.setTextColor(...DARK);
        doc.text(String(guia), 19, y + 12);
        if (order.c807_tipo_servicio) {
            doc.setFont(BODY, "normal"); doc.setFontSize(8); doc.setTextColor(...MUTED);
            doc.text(`Servicio ${order.c807_tipo_servicio}`, W - 20, y + 12, { align: "right" });
        }
        y += 20;
    } else if (order.payment_method === "transferencia" && order.needs_shipping) {
        // Placeholder for transfer orders that haven't been confirmed yet.
        doc.setFillColor(...CREAM);
        doc.rect(15, y, W - 30, 14, "F");
        doc.setDrawColor(...GOLD); doc.setLineWidth(0.3);
        doc.rect(15, y, W - 30, 14);
        doc.setFont(BODY, "bold"); doc.setFontSize(7); doc.setTextColor(...GOLD);
        doc.text("NÚMERO DE GUÍA", 19, y + 5);
        doc.setFont(BODY, "italic"); doc.setFontSize(8); doc.setTextColor(...MUTED);
        doc.text("Se generará y enviará a tu correo tras confirmar la transferencia.", 19, y + 11);
        y += 18;
    }

    // Address block — country-aware
    const oc = (order.country || "SV").toUpperCase();
    const storeAddr = oc === "US"
        ? (settings.address_us || settings.address || "Envíos a todo Estados Unidos vía USPS")
        : (settings.address_sv || settings.address || "Sonsonate, El Salvador");
    const addressText = order.needs_shipping
        ? (order.shipping_address || "Sin dirección registrada")
        : storeAddr;
    drawSection(order.needs_shipping ? "DIRECCIÓN DE ENVÍO" : "DIRECCIÓN DE LA TIENDA", 15, y, W - 30, [[addressText, false, true]]);
    y += 26;

    // Items table
    doc.setFont(BODY, "bold");
    doc.setFontSize(8);
    doc.setTextColor(...GOLD);
    doc.text("PRODUCTOS", 15, y);
    y += 5;

    doc.setFillColor(...DARK);
    doc.rect(15, y, W - 30, 7, "F");
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text("SKU", 18, y + 4.7);
    doc.text("PRODUCTO", 42, y + 4.7);
    doc.text("CANT.", W - 60, y + 4.7, { align: "right" });
    doc.text("PRECIO", W - 38, y + 4.7, { align: "right" });
    doc.text("SUBTOTAL", W - 18, y + 4.7, { align: "right" });
    y += 7;

    doc.setTextColor(...DARK);
    doc.setFont(BODY, "normal");
    (order.items || []).forEach((it, i) => {
        const bg = i % 2 === 0 ? [252, 250, 245] : [255, 255, 255];
        doc.setFillColor(...bg);
        doc.rect(15, y, W - 30, 9, "F");
        doc.setFontSize(8);
        doc.setFont("courier", "normal");
        doc.text(it.sku || "—", 18, y + 6);
        doc.setFont(BODY, "normal");
        const name = (it.nombre || "").length > 38 ? (it.nombre || "").slice(0, 36) + "…" : (it.nombre || "");
        doc.text(name, 42, y + 6);
        doc.text(String(it.quantity || 1), W - 60, y + 6, { align: "right" });
        doc.text(fmt(it.precio || 0), W - 38, y + 6, { align: "right" });
        doc.setFont(BODY, "bold");
        doc.text(fmt(it.subtotal || 0), W - 18, y + 6, { align: "right" });
        doc.setFont(BODY, "normal");
        y += 9;
    });

    y += 4;
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.4);
    doc.line(W - 90, y, W - 15, y);
    y += 6;

    // Totals
    const subtotal = order.subtotal != null ? order.subtotal : order.total;
    const shipping = order.shipping || 0;
    doc.setFontSize(FS);
    doc.setTextColor(...TEXT);
    doc.setFont(BODY, "normal");
    doc.text("Subtotal", W - 65, y);
    doc.text(fmt(subtotal), W - 18, y, { align: "right" });
    y += 5;
    doc.text("Envío", W - 65, y);
    doc.text(shipping > 0 ? fmt(shipping) : "Gratis / Retiro", W - 18, y, { align: "right" });
    y += 8;

    // Grand total
    doc.setFillColor(...DARK);
    doc.rect(W - 90, y - 6, 75, 12, "F");
    doc.setFont(BODY, "bold");
    doc.setFontSize(11);
    doc.setTextColor(...lighten(GOLD, 40));
    doc.text("TOTAL USD", W - 87, y + 1);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(13);
    doc.text(fmt(order.total), W - 18, y + 1, { align: "right" });
    y += 14;

    // Notes
    if (order.notes) {
        y += 2;
        drawSection("NOTAS DEL CLIENTE", 15, y, W - 30, [[order.notes, false, true]]);
        y += 22;
    }

    // BANK INFO BLOCK
    const isTransfer = (order.payment_method || "").toLowerCase() === "transferencia";
    if (isTransfer && cfg.show_bank_block !== false) {
        const banks = settings.bank_accounts || [];
        const bankLines = banks.length > 0
            ? banks.flatMap(b => [
                `${(b.bank_name || "").toUpperCase()}`,
                `${b.account_holder || ""} · ${b.account_type || "Ahorro"}`,
                `Cuenta: ${b.account_number || "—"}`,
                b.notes ? `${b.notes}` : "",
                "",
            ]).filter(Boolean)
            : (settings.bank_info ? settings.bank_info.split("\n") : ["Solicita los datos bancarios por WhatsApp."]);
        const blockH = Math.min(64, 12 + bankLines.length * 4);
        doc.setFillColor(...CREAM);
        doc.rect(15, y, W - 30, blockH, "F");
        doc.setDrawColor(...GOLD); doc.setLineWidth(0.3);
        doc.rect(15, y, W - 30, blockH);
        doc.setFont(BODY, "bold"); doc.setFontSize(9); doc.setTextColor(...GOLD);
        doc.text(cfg.bank_block_title || "INSTRUCCIONES DE PAGO POR TRANSFERENCIA", 19, y + 6);
        doc.setFont(BODY, "normal"); doc.setFontSize(8); doc.setTextColor(...DARK);
        bankLines.slice(0, 12).forEach((ln, i) => doc.text(String(ln).slice(0, 100), 19, y + 12 + (i * 4)));
        y += blockH + 4;
    }

    // SOCIAL MEDIA BLOCK — prefer uploaded icons (settings.social_items), fallback to old text block
    if (cfg.show_social_block !== false) {
        const items = (settings.social_items || []).filter((it) => it && it.icon && (it.url || it.alt));
        if (items.length > 0) {
            // New variant: uploaded icons + handle/label
            const lines = items.slice(0, 6).map((it) => {
                // derive a clean handle for display
                let handle = it.alt || "";
                if (!handle && it.url) {
                    try {
                        const u = new URL(it.url);
                        handle = u.hostname.replace(/^www\./, "") + (u.pathname !== "/" ? u.pathname : "");
                    } catch { handle = it.url; }
                }
                return { icon: it.icon, handle };
            });
            const blockH = Math.max(22, 10 + lines.length * 6);
            doc.setFillColor(...DARK);
            doc.rect(15, y, W - 30, blockH, "F");
            doc.setFillColor(...GOLD);
            doc.rect(15, y, 1.5, blockH, "F");
            doc.setFont(DISP, "italic"); doc.setFontSize(11); doc.setTextColor(...lighten(GOLD, 40));
            doc.text(cfg.social_block_title || "Síguenos y mantente al tanto de nuestras novedades", 21, y + 7);

            lines.forEach((ln, i) => {
                const cy = y + 13 + (i * 6);
                // Detect image format from data URL prefix to avoid throw/catch noise.
                const fmt = (ln.icon || "").startsWith("data:image/jpeg") || (ln.icon || "").startsWith("data:image/jpg") ? "JPEG" : "PNG";
                try { doc.addImage(ln.icon, fmt, 21, cy - 3.5, 5, 5); }
                catch { /* unsupported icon format — skip the image, keep the handle text */ }
                doc.setFont(BODY, "normal"); doc.setFontSize(10); doc.setTextColor(255, 255, 255);
                doc.text(String(ln.handle || ""), 29, cy);
            });
            y += blockH + 4;
        } else {
            // Legacy fallback (no uploaded icons): keep old text+swatch behavior
            let lines = (cfg.social_lines || []).filter(l => l && l.show !== false && (l.label || "").trim() && (l.value || "").trim());
            if (lines.length === 0) {
                const sl = settings.social_links || {};
                const fb = (sl.facebook || "Terra Boutique").replace(/https?:\/\/(www\.)?facebook\.com\//, "");
                const ig = (sl.instagram || "terra_boutique2026").replace(/https?:\/\/(www\.)?instagram\.com\//, "").replace("@", "");
                lines = [
                    { label: "Facebook", value: fb },
                    { label: "Instagram", value: `@${ig}` },
                ];
            }
            const SOCIAL_ICONS = {
                facebook:  { bg: [59, 89, 152],  fg: [255, 255, 255], char: "f" },
                instagram: { bg: [225, 48, 108], fg: [255, 255, 255], char: "◯" },
                tiktok:    { bg: [0, 0, 0],      fg: [255, 255, 255], char: "♪" },
                whatsapp:  { bg: [37, 211, 102], fg: [255, 255, 255], char: "✓" },
                twitter:   { bg: [29, 161, 242], fg: [255, 255, 255], char: "t" },
                x:         { bg: [0, 0, 0],      fg: [255, 255, 255], char: "X" },
                youtube:   { bg: [255, 0, 0],    fg: [255, 255, 255], char: "▶" },
                email:     { bg: [120, 120, 120],fg: [255, 255, 255], char: "@" },
                web:       { bg: [200, 168, 80], fg: [0, 0, 0],       char: "●" },
            };
            const iconFor = (label) => {
                const k = (label || "").toLowerCase().trim();
                for (const key of Object.keys(SOCIAL_ICONS)) {
                    if (k.includes(key)) return SOCIAL_ICONS[key];
                }
                return SOCIAL_ICONS.web;
            };

            const blockH = Math.max(22, 10 + lines.length * 6);
            doc.setFillColor(...DARK);
            doc.rect(15, y, W - 30, blockH, "F");
            doc.setFillColor(...GOLD);
            doc.rect(15, y, 1.5, blockH, "F");
            doc.setFont(DISP, "italic"); doc.setFontSize(11); doc.setTextColor(...lighten(GOLD, 40));
            doc.text(cfg.social_block_title || "Síguenos y mantente al tanto de nuestras novedades", 21, y + 7);

            lines.slice(0, 6).forEach((ln, i) => {
                const icon = iconFor(ln.label);
                const cy = y + 13 + (i * 6);
                doc.setFillColor(...icon.bg);
                doc.roundedRect(21, cy - 3.5, 5, 5, 1, 1, "F");
                doc.setFont(BODY, "bold");
                doc.setFontSize(8);
                doc.setTextColor(...icon.fg);
                doc.text(icon.char, 23.5, cy, { align: "center" });
                doc.setFont(BODY, "normal"); doc.setFontSize(10); doc.setTextColor(255, 255, 255);
                doc.text(String(ln.value || ""), 29, cy);
            });
            y += blockH + 4;
        }
    }

    // Footer
    const footerY = doc.internal.pageSize.getHeight() - 18;
    doc.setFillColor(...DARK);
    doc.rect(0, footerY, W, 18, "F");
    doc.setFont(DISP, "italic");
    doc.setFontSize(10);
    doc.setTextColor(...lighten(GOLD, 40));
    doc.text(cfg.footer_text || "Gracias por elegir Terra Boutique", W / 2, footerY + 8, { align: "center" });
    doc.setFont(BODY, "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(200, 200, 200);
    const subFooter = cfg.footer_subtext || `Documento generado el ${new Date().toLocaleString("es-SV")}`;
    doc.text(subFooter, W / 2, footerY + 14, { align: "center" });

    const filename = `${(cfg.header_brand || "Terra-Boutique").replace(/\s+/g, "-")}-${(order._id || order.order_id || "pedido").slice(0, 8)}.pdf`;
    doc.save(filename);
}

// Sample order for the live preview in admin settings
export const SAMPLE_RECEIPT_ORDER = {
    _id: "PED-PREVIEW-001",
    created_at: new Date().toISOString(),
    customer_name: "María",
    customer_lastname: "Hernández",
    customer_phone: "+503 7777-7777",
    user_email: "cliente@ejemplo.com",
    payment_method: "transferencia",
    needs_shipping: true,
    shipping_address: "Col. Escalón, Pasaje 1 #23, San Salvador",
    status: "pending",
    items: [
        { sku: "TB-001", nombre: "Vestido de gala dorado", quantity: 1, precio: 89.99, subtotal: 89.99 },
        { sku: "TB-002", nombre: "Bolso de cuero premium", quantity: 2, precio: 45.00, subtotal: 90.00 },
    ],
    subtotal: 179.99,
    shipping: 4.99,
    total: 184.98,
    notes: "Por favor entregar después de las 3 PM.",
};
